import type { ReactNode } from 'react';
import MUIAvatar, { AvatarProps } from '@mui/material/Avatar';
import { SxProps, Theme } from '../styles';

import getUserColor from '../Theme/getUserColor';
import Typography, { Variant } from '../Typography';

export type AvatarSizeProp = 'xs' | 's' | 'm' | 'l';

export const getInitials = (fullName: string) => {
  const nameParts = fullName.split(' ');
  const firstPart = nameParts[0];
  const lastPart = nameParts.length >= 2 ? nameParts[nameParts.length - 1] : null;
  let initials = firstPart.charAt(0);

  if (lastPart) {
    initials += lastPart.charAt(0);
  }

  return initials;
};
const realAvatarUrl = (avatarUrl: string) =>
  !!avatarUrl && !(avatarUrl.match(/avatar_defaut\.png/) || avatarUrl.match(/gravatar\.com/))
    ? avatarUrl
    : '';

export interface Props extends AvatarProps {
  children?: ReactNode;
  userName: string;
  userId: string | number;
  shouldHideAriaLabel?: boolean;
  size?: AvatarSizeProp;
  src?: string;
  sx?: SxProps<Theme>;
}

const Avatar = ({
  children,
  size = 's',
  userName,
  userId,
  src = '',
  sx = {},
  shouldHideAriaLabel = false,
  ...otherProps
}: Props) => {
  const avatarLayoutProps: {
    [size: string]: {
      avatarSize: number;
      initialsVariant: Variant;
    };
  } = {
    xs: {
      avatarSize: 32,
      initialsVariant: 'subheadingSmall',
    },
    s: {
      avatarSize: 40,
      initialsVariant: 'subheadingSmall',
    },
    m: {
      avatarSize: 64,
      initialsVariant: 'subheading',
    },
    l: {
      avatarSize: 80,
      initialsVariant: 'subheading',
    },
  };

  return (
    <MUIAvatar
      data-testid="avatar-root"
      sx={{
        width: avatarLayoutProps[size].avatarSize,
        height: avatarLayoutProps[size].avatarSize,
        bgcolor: (theme: Theme) => getUserColor(theme.palette, userId, userName.toString()),
        textDecoration: 'none',
        ...sx,
      }}
      alt=""
      src={realAvatarUrl(src)}
      {...otherProps}
    >
      <Typography
        aria-label={shouldHideAriaLabel ? '' : userName}
        variant={avatarLayoutProps[size].initialsVariant}
        sx={{ color: 'text.onContrasted', textTransform: 'uppercase' }}
      >
        {children || getInitials(userName)}
      </Typography>
    </MUIAvatar>
  );
};

export default Avatar;
